import React, {useState, useEffect, useMemo} from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { geocodeByPlaceId } from 'react-places-autocomplete';
import { useTranslation } from "react-i18next";
import Box from '@mui/material/Box';
import {MAIN_COLOR} from "../common/sharedFunctions"
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  iconRtl: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
  },
  inputRtl: {
    "& label": {
      right: 75,
      left: "auto"
    },
    "& legend": {
      textAlign: "right",
      marginRight:60
    }
  },
  textField: {
    "& label.Mui-focused": {
      color: MAIN_COLOR,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: MAIN_COLOR,
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: MAIN_COLOR,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: MAIN_COLOR,
      },
    },
  },
}));

export default function GoogleMapsAutoComplete(props) {
  const classes = useStyles();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const { t } = useTranslation();
  const { i18n  } = useTranslation();
  const isRTL = i18n.dir();
  const [gpsLoading, setGpsLoading] = useState(false);

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    [],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const handleGetCurrentLocation = (e) => {
    e.stopPropagation();
    if (!navigator.geolocation) {
      alert(t('geolocation_not_supported', 'Geolocation is not supported by your browser.'));
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
              const address = results[0].formatted_address;
              const placeId = results[0].place_id;
              const newValue = {
                description: address,
                place_id: placeId,
                coords: { lat: latitude, lng: longitude },
                placeDetails: results[0],
                structured_formatting: {
                  main_text: address,
                  secondary_text: '',
                  main_text_matched_substrings: []
                }
              };
              setValue(newValue);
              props.onChange(newValue);
            } else {
              alert(t('reverse_geocode_error', 'Unable to retrieve address for current location.'));
            }
            setGpsLoading(false);
          });
        } else {
          alert(t('google_maps_not_loaded', 'Google Maps API is not loaded yet.'));
          setGpsLoading(false);
        }
      },
      (error) => {
        let msg = t('gps_error', 'Error getting your current location.');
        if (error.code === 1) {
          msg = t('gps_permission_denied', 'Location access permission denied.');
        } else if (error.code === 2) {
          msg = t('gps_position_unavailable', 'Location position unavailable.');
        } else if (error.code === 3) {
          msg = t('gps_timeout', 'Location request timed out.');
        }
        alert(msg);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return (
    <Autocomplete
      style={props.style}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={props.value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue && newValue.place_id) {
          geocodeByPlaceId(newValue.place_id)
            .then(results => {
              if (results.length > 0) {
                newValue.coords = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() }
                newValue.placeDetails = results[0];
              }
              props.onChange(newValue);
            })
            .catch(error => alert(t('google_places_error')));
        } else {
          props.onChange(newValue);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={props.placeholder}
          variant={props.variant}
          className={[isRTL==='rtl'? classes.inputRtl:classes.commonInputStyle, classes.textField].join(" ")}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {gpsLoading ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                ) : (
                  <IconButton
                    onClick={handleGetCurrentLocation}
                    onMouseDown={(e) => e.stopPropagation()}
                    edge="end"
                    title={t('use_current_location', 'Use Current Location')}
                    size="small"
                    style={{ color: 'var(--primary)', marginRight: 8 }}
                  >
                    <GpsFixedIcon />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
        />
      )}

      renderOption={(props, option) => {
        const matches =
          (option.structured_formatting && option.structured_formatting.main_text_matched_substrings) || [];

        const mainText = (option.structured_formatting && option.structured_formatting.main_text) || option.description || '';
        const secondaryText = (option.structured_formatting && option.structured_formatting.secondary_text) || '';

        const parts = parse(
          mainText,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))}

                {secondaryText ? (
                  <Typography variant="body2" color="text.secondary">
                    {secondaryText}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}